import { useState, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import { api } from '../lib/api'
import { hidePurchases } from '../lib/native'
import { setCachedUser } from '../lib/auth'
import { configureLock, setVeilLocal } from '../lib/security'
import type { User, Plan } from '../lib/types'

const fmtBytes = (n: number) => n >= 1024 ** 3 ? `${(n / 1024 ** 3).toFixed(1)} GB` : `${Math.round(n / 1024 / 1024)} MB`

export default function Profile({ user, onUpdate }: { user: User; onUpdate: (u: User) => void }) {
  const [name, setName] = useState(user.name || '')
  const [busy, setBusy] = useState(false)
  const [saved, setSaved] = useState(false)
  const [plans, setPlans] = useState<Plan[]>([])
  const [subMsg, setSubMsg] = useState('')
  const [checkingOut, setCheckingOut] = useState('')
  const [showErase, setShowErase] = useState(false)
  const [eraseConfirm, setEraseConfirm] = useState('')
  const [eraseMsg, setEraseMsg] = useState('')
  const [erasing, setErasing] = useState(false)
  const [redemptions, setRedemptions] = useState<Awaited<ReturnType<typeof api.myRedemptions>>>([])
  const [promoInput, setPromoInput] = useState('')
  const [promoMsg, setPromoMsg] = useState('')
  const [credits, setCredits] = useState<Awaited<ReturnType<typeof api.getCredits>> | null>(null)
  const [creditMsg, setCreditMsg] = useState('')
  const [buyingPack, setBuyingPack] = useState('')
  const [pushState, setPushState] = useState<'unsupported' | 'off' | 'on' | 'unavailable'>('unsupported')
  const [digestOn, setDigestOn] = useState((user as User & { digest_opt?: number }).digest_opt !== 0)
  const [lock, setLock] = useState<{ enabled: boolean; timeout_mins: number; privacy_veil: boolean } | null>(null)
  const [lockForm, setLockForm] = useState<{ mode: 'set' | 'change' | 'disable'; pass: string; current: string } | null>(null)
  const [lockMsg, setLockMsg] = useState('')
  const [store, setStore] = useState<Awaited<ReturnType<typeof api.getStorage>> | null>(null)
  const [storeMsg, setStoreMsg] = useState('')
  const [params, setParams] = useSearchParams()

  const loadCredits = () => api.getCredits().then(setCredits).catch(() => {})

  const applyLock = async () => {
    if (!lockForm) return
    setLockMsg('')
    try {
      if (lockForm.mode === 'disable') {
        await api.setLock({ disable: true, current_passcode: lockForm.current })
        setLock(l => l ? { ...l, enabled: false } : l)
        configureLock(false, lock?.timeout_mins || 5)
      } else {
        if (!/^\d{4,8}$/.test(lockForm.pass)) { setLockMsg('Passcode must be 4–8 digits.'); return }
        await api.setLock({ passcode: lockForm.pass, current_passcode: lockForm.current || undefined })
        setLock(l => l ? { ...l, enabled: true } : l)
        configureLock(true, lock?.timeout_mins || 5)
      }
      setLockForm(null)
    } catch (e) {
      setLockMsg(e instanceof Error ? e.message.replace(/^API \d+: /, '').replace(/[{}"]|error:/g, '') : 'Failed')
    }
  }

  useEffect(() => {
    api.listPlans().then(setPlans).catch(() => {})
    api.myRedemptions().then(setRedemptions).catch(() => {})
    api.getLock().then(setLock).catch(() => {})
    api.getStorage().then(setStore).catch(() => {})
    loadCredits()
    if ('serviceWorker' in navigator && 'PushManager' in window) {
      api.getPushStatus().then(async s => {
        if (!s.key) { setPushState('unavailable'); return }
        const reg = await navigator.serviceWorker.getRegistration()
        const sub = await reg?.pushManager.getSubscription()
        setPushState(sub && s.subscribed ? 'on' : 'off')
      }).catch(() => setPushState('unavailable'))
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const togglePush = async () => {
    try {
      const reg = await navigator.serviceWorker.getRegistration()
      if (!reg) return
      if (pushState === 'on') {
        const sub = await reg.pushManager.getSubscription()
        if (sub) { await api.unsubscribePush(sub.endpoint); await sub.unsubscribe() }
        setPushState('off')
        return
      }
      const { key } = await api.getPushStatus()
      if (!key) { setPushState('unavailable'); return }
      const raw = Uint8Array.from(atob(key.replace(/-/g, '+').replace(/_/g, '/')), c => c.charCodeAt(0))
      const sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: raw })
      const j = sub.toJSON()
      await api.subscribePush({ endpoint: sub.endpoint, keys: { p256dh: j.keys?.p256dh || '', auth: j.keys?.auth || '' } })
      setPushState('on')
    } catch { /* permission denied or unsupported — leave state */ }
  }

  const toggleDigest = async () => {
    const next = !digestOn
    setDigestOn(next)
    try { await api.updatePrefs({ digest_opt: next }) } catch { setDigestOn(!next) }
  }

  const buyPack = async (packId: string) => {
    setBuyingPack(packId); setCreditMsg('')
    try {
      const { url } = await api.buyCredits(packId)
      window.location.href = url
    } catch (e) {
      setCreditMsg(e instanceof Error ? e.message.replace(/^API \d+: /, '').replace(/[{}"]|error:/g, '') : 'Checkout failed')
      setBuyingPack('')
    }
  }

  const toggleAutoReload = async () => {
    if (!credits) return
    const next = !credits.auto_reload
    setCredits({ ...credits, auto_reload: next })
    try { await api.setCreditPrefs({ auto_reload: next, auto_reload_pack: credits.auto_reload_pack || 'spark' }) }
    catch { setCredits({ ...credits, auto_reload: !next }) }
  }

  const redeem = async () => {
    setPromoMsg('')
    try {
      const res = await api.redeemPromo(promoInput.trim())
      setPromoMsg(res.message)
      setPromoInput('')
      api.myRedemptions().then(setRedemptions).catch(() => {})
    } catch (e) {
      setPromoMsg(e instanceof Error ? e.message.replace(/^API \d+: /, '').replace(/[{}"]|error:/g, '') : 'Could not redeem')
    }
  }

  // Returning from Stripe Checkout — verify and activate
  useEffect(() => {
    const sessionId = params.get('session_id')
    if (!sessionId) {
      if (params.get('checkout') === 'cancelled') setSubMsg('Checkout cancelled — no charge was made.')
      return
    }
    setSubMsg('Confirming your payment…')
    api.verifyCheckout(sessionId).then(async res => {
      if (res.activated && res.kind === 'credits') {
        setCreditMsg(res.already_applied
          ? 'This purchase was already applied to your balance.'
          : `⚡ ${res.credits} Sage credits added to your reserve.`)
        loadCredits()
      } else if (res.activated) {
        setSubMsg(`🎉 Welcome to ${res.plan} — your constellation just got bigger.`)
        const me = await api.me()
        setCachedUser(me); onUpdate(me)
      } else {
        setSubMsg(`Payment status: ${res.status || 'incomplete'} — refresh in a moment or contact the keeper.`)
      }
      setParams({}, { replace: true })
    }).catch(() => setSubMsg('Could not verify the payment session.'))
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const save = async (e: React.FormEvent) => {
    e.preventDefault()
    setBusy(true)
    try {
      const updated = await api.updateProfile(name.trim())
      setCachedUser(updated)
      onUpdate(updated)
      setSaved(true)
      setTimeout(() => setSaved(false), 2000)
    } finally { setBusy(false) }
  }

  const choosePlan = async (planId: string) => {
    setSubMsg('')
    if (planId === 'seedling' || planId === (user.plan || 'seedling')) {
      const res = await api.subscribe(planId)
      setSubMsg(res.message)
      return
    }
    setCheckingOut(planId)
    try {
      const { url, founder } = await api.checkout(planId)
      if (founder) setSubMsg('Founder pricing applied — locked in forever. Redirecting to secure checkout…')
      window.location.href = url
    } catch (e) {
      setSubMsg(e instanceof Error ? e.message.replace(/^API \d+: /, '') : 'Checkout failed')
      setCheckingOut('')
    }
  }

  return (
    <div className="max-w-2xl mx-auto px-4 py-8">
      <h1 className="font-display text-3xl font-bold text-slate-100 mb-8">Profile</h1>

      <form onSubmit={save} className="card-glass p-6 mb-6">
        <label className="text-xs uppercase tracking-wider text-slate-500">Email</label>
        <p className="text-slate-300 mb-4">{user.email}</p>
        <label className="text-xs uppercase tracking-wider text-slate-500">Display name</label>
        <input value={name} onChange={e => setName(e.target.value)} placeholder="Your name" className="input-dark mt-1 mb-4" />
        <button type="submit" disabled={busy} className="btn-gold">{saved ? '✓ Saved' : busy ? 'Saving…' : 'Save'}</button>
      </form>

      <div className="card-glass p-6">
        <h2 className="font-display text-lg font-semibold text-slate-100 mb-1">Subscription</h2>
        <p className="text-sm text-slate-500 mb-4">
          Current plan: <span className="text-gold-300 font-semibold capitalize">{user.plan || 'seedling'}</span>
        </p>
        {hidePurchases() ? (
          <p className="text-sm text-slate-500">Your plan is managed on your account.</p>
        ) : (
        <div className="grid sm:grid-cols-3 gap-3">
          {plans.map(p => (
            <button key={p.id} onClick={() => choosePlan(p.id)} disabled={!!checkingOut}
              className={`text-left p-4 rounded-xl border transition-colors ${
                (user.plan || 'seedling') === p.id
                  ? 'border-gold-500/50 bg-gold-500/10'
                  : 'border-white/10 bg-white/[0.03] hover:border-gold-500/30'
              }`}>
              <p className="font-display font-semibold text-slate-100">{p.name}</p>
              <p className="text-gold-300 text-sm font-bold">${p.price}<span className="text-slate-500 font-normal">/{p.period}</span></p>
              <p className="text-[10px] text-slate-500 mt-1">
                {(user.plan || 'seedling') === p.id ? 'current plan'
                  : checkingOut === p.id ? 'opening checkout…'
                  : p.price > 0 ? 'secure Stripe checkout' : '✳ early-access special — limited time'}
              </p>
            </button>
          ))}
        </div>
        )}
        {subMsg && <p className="text-xs text-gold-300 mt-3">{subMsg}</p>}
        {redemptions.length > 0 && (
          <p className="text-[11px] text-emerald-400 mt-2">
            Active offers: {redemptions.map(r => r.kind === 'lockin' ? `${r.lock_years}yr lock` : `${r.percent_off}% ${r.kind}`).join(' · ')}
          </p>
        )}
        {!hidePurchases() && <div className="flex gap-2 mt-3">
          <input value={promoInput} onChange={e => setPromoInput(e.target.value)} placeholder="Promo / discount code"
            className="input-dark !py-2 text-sm flex-1 uppercase" />
          <button onClick={redeem} disabled={!promoInput.trim()} className="btn-ghost !py-2 text-sm">Redeem</button>
        </div>}
        {promoMsg && <p className="text-xs text-gold-300 mt-2">{promoMsg}</p>}
        {!hidePurchases() && <p className="text-[11px] text-slate-600 mt-3">
          Prices show the market rate struck through and the special rate locked until September 2026. Founder accounts (joined before Sep 2026) get an even lower rate; lock-in codes hold your rate for 1–3 years.
        </p>}
      </div>

      {/* Sage Credits (v0.9.0) */}
      <div className="card-glass p-6 mt-6">
        <h2 className="font-display text-lg font-semibold text-slate-100 mb-1">Sage Credits</h2>
        <p className="text-xs text-slate-500 mb-4">
          Every account starts with 100 credits. Your plan includes a monthly allowance of Sage messages;
          beyond it, each message uses 1 credit.
        </p>
        {credits ? (
          <>
            <div className="flex items-end justify-between mb-1.5">
              <div>
                <p className="text-2xl font-display font-bold text-gold-300">{credits.balance}<span className="text-sm text-slate-500 font-normal ml-1.5">credits in reserve</span></p>
              </div>
              <p className="text-[11px] text-slate-500 text-right">
                {credits.included_limit > 0
                  ? <>plan allowance: <span className="text-slate-300">{Math.min(credits.included_used, credits.included_limit)}/{credits.included_limit}</span> used · renews {credits.resets_at?.slice(0, 10)}</>
                  : 'no monthly allowance on this tier — messages draw from credits'}
              </p>
            </div>
            {credits.included_limit > 0 && (
              <div className="h-1.5 rounded-full bg-white/[0.07] overflow-hidden mb-4">
                <div className={`h-full ${credits.included_used >= credits.included_limit ? 'bg-amber-500/70' : 'bg-gradient-to-r from-emerald-500/70 to-gold-500/70'}`}
                  style={{ width: `${Math.min(100, (credits.included_used / credits.included_limit) * 100)}%` }} />
              </div>
            )}
            {!hidePurchases() && <div className="grid sm:grid-cols-3 gap-3 mb-4">
              {credits.packs.map(pk => (
                <button key={pk.id} onClick={() => buyPack(pk.id)} disabled={!!buyingPack}
                  className="text-left p-3.5 rounded-xl border border-white/10 bg-white/[0.03] hover:border-gold-500/40 transition-colors">
                  <p className="font-display font-semibold text-slate-100 capitalize">{pk.id}</p>
                  <p className="text-gold-300 text-sm font-bold">{pk.credits} credits</p>
                  {pk.discounted_cents != null && pk.discounted_cents < pk.cents ? (
                    <p className="text-xs">
                      <span className="text-slate-500 line-through mr-1">${(pk.cents / 100).toFixed(2)}</span>
                      <span className="text-emerald-400 font-semibold">${(pk.discounted_cents / 100).toFixed(2)}</span>
                      <span className="text-slate-600 ml-1">({pk.promo_code})</span>
                    </p>
                  ) : (
                    <p className="text-xs text-slate-400">${(pk.cents / 100).toFixed(2)}</p>
                  )}
                  <p className="text-[10px] text-slate-600 mt-1">{buyingPack === pk.id ? 'opening checkout…' : 'secure Stripe checkout'}</p>
                </button>
              ))}
            </div>}
            <label className={`flex items-center gap-2.5 text-sm text-slate-300 cursor-pointer ${hidePurchases() ? 'hidden' : ''}`}>
              <input type="checkbox" checked={credits.auto_reload} onChange={toggleAutoReload} className="accent-amber-400" />
              <span>Auto-reload: email me a one-tap reload link when my reserve drops below 10 credits</span>
            </label>
            {credits.auto_reload && (
              <div className="flex items-center gap-2 mt-2 ml-6">
                <span className="text-xs text-slate-500">Preferred pack:</span>
                {credits.packs.map(pk => (
                  <button key={pk.id}
                    onClick={() => { setCredits({ ...credits, auto_reload_pack: pk.id }); api.setCreditPrefs({ auto_reload: true, auto_reload_pack: pk.id }).catch(() => {}) }}
                    className={`text-xs px-2.5 py-1 rounded-lg border capitalize ${credits.auto_reload_pack === pk.id ? 'border-gold-500/50 bg-gold-500/10 text-gold-300' : 'border-white/10 text-slate-400 hover:border-white/25'}`}>
                    {pk.id}
                  </button>
                ))}
              </div>
            )}
            {creditMsg && <p className="text-xs text-gold-300 mt-3">{creditMsg}</p>}
            <p className="text-[11px] text-slate-600 mt-3">
              Credit discount codes redeem in the Subscription box above and apply automatically to pack prices here.
            </p>
          </>
        ) : (
          <p className="text-xs text-slate-500">Loading your balance…</p>
        )}
      </div>

      {/* Notifications (v0.9.0) */}
      <div className="card-glass p-6 mt-6">
        <h2 className="font-display text-lg font-semibold text-slate-100 mb-1">Notifications</h2>
        <p className="text-xs text-slate-500 mb-4">How the constellation reaches you.</p>
        <div className="space-y-3">
          <label className="flex items-center gap-2.5 text-sm text-slate-300 cursor-pointer">
            <input type="checkbox" checked={digestOn} onChange={toggleDigest} className="accent-amber-400" />
            <span>Weekly email digest — new souls, stories, memories &amp; upcoming gatherings</span>
          </label>
          <div className="flex items-center gap-2.5 text-sm text-slate-300">
            {pushState === 'unsupported' || pushState === 'unavailable' ? (
              <p className="text-xs text-slate-600">Push notifications {pushState === 'unsupported' ? 'are not supported by this browser' : 'are not configured yet'}.</p>
            ) : (
              <label className="flex items-center gap-2.5 cursor-pointer">
                <input type="checkbox" checked={pushState === 'on'} onChange={togglePush} className="accent-amber-400" />
                <span>Push notifications on this device — family meet-ups &amp; gatherings</span>
              </label>
            )}
          </div>
        </div>
      </div>

      {/* Sanctum Lock & Privacy Veil (v0.10.0) */}
      <div className="card-glass p-6 mt-6">
        <h2 className="font-display text-lg font-semibold text-slate-100 mb-1">Security — Sanctum Lock &amp; Privacy Veil</h2>
        <p className="text-xs text-slate-500 mb-4">
          The lock seals the screen after idle time (passcode checked server-side) — for devices left open.
          The veil masks real names with aliases/initials — for screens in public view. Vault media stays end-to-end encrypted regardless.
        </p>
        <div className="space-y-3">
          {lock && (
            <>
              <div className="flex items-center gap-3 flex-wrap">
                {lock.enabled ? (
                  <>
                    <span className="text-xs px-2 py-1 rounded-lg bg-emerald-500/10 border border-emerald-500/25 text-emerald-300">🔒 Lock is ON</span>
                    <select value={lock.timeout_mins} onChange={async e => { const t = Number(e.target.value); setLock({ ...lock, timeout_mins: t }); await api.setLock({ timeout_mins: t }).catch(() => {}) }}
                      className="input-dark !py-1.5 text-xs w-40">
                      {[1, 2, 5, 10, 15, 30, 60].map(m => <option key={m} value={m}>lock after {m} min idle</option>)}
                    </select>
                    <button onClick={() => setLockForm({ mode: 'disable', pass: '', current: '' })} className="text-xs text-red-400/80 hover:text-red-400">Disable…</button>
                    <button onClick={() => setLockForm({ mode: 'change', pass: '', current: '' })} className="text-xs text-slate-400 hover:text-slate-200">Change passcode…</button>
                  </>
                ) : (
                  <button onClick={() => setLockForm({ mode: 'set', pass: '', current: '' })} className="btn-ghost !py-2 text-sm">🔒 Set up screen lock</button>
                )}
              </div>
              {lockForm && (
                <div className="rounded-xl border border-gold-500/25 bg-gold-500/[0.04] p-3 flex flex-wrap items-center gap-2">
                  {lockForm.mode !== 'set' && (
                    <input type="password" inputMode="numeric" placeholder="Current passcode" value={lockForm.current}
                      onChange={e => setLockForm({ ...lockForm, current: e.target.value.replace(/\D/g, '').slice(0, 8) })}
                      className="input-dark !py-1.5 text-xs w-36" />
                  )}
                  {lockForm.mode !== 'disable' && (
                    <input type="password" inputMode="numeric" placeholder="New passcode (4–8 digits)" value={lockForm.pass}
                      onChange={e => setLockForm({ ...lockForm, pass: e.target.value.replace(/\D/g, '').slice(0, 8) })}
                      className="input-dark !py-1.5 text-xs w-44" />
                  )}
                  <button onClick={applyLock} className="btn-gold !py-1.5 !px-3 text-xs">{lockForm.mode === 'disable' ? 'Disable lock' : 'Save'}</button>
                  <button onClick={() => setLockForm(null)} className="btn-ghost !py-1.5 !px-3 text-xs">Cancel</button>
                  {lockMsg && <p className="text-xs text-gold-300 w-full">{lockMsg}</p>}
                </div>
              )}
              <label className="flex items-center gap-2.5 text-sm text-slate-300 cursor-pointer">
                <input type="checkbox" checked={lock.privacy_veil} onChange={async e => { const on = e.target.checked; setLock({ ...lock, privacy_veil: on }); setVeilLocal(on); await api.setLock({ privacy_veil: on }).catch(() => {}) }}
                  className="accent-violet-400" />
                <span>🎭 Privacy Veil — show aliases/initials instead of real names (quick-toggle also lives in the top bar)</span>
              </label>
            </>
          )}
          <p className="text-[11px] text-slate-600">
            Honest scope: the lock defends a device someone else picks up, not a stolen session — for that, sign out everywhere below.
            E2EE: Legacy Vault media is encrypted on YOUR device (AES-256-GCM); the server holds only ciphertext.
          </p>
          <div className="pt-2 border-t border-white/[0.06]">
            <button onClick={async () => {
              if (!confirm('Sign out on ALL devices? Every signed-in session — including any lost or stolen device — will be logged out immediately. You’ll need to sign in again here.')) return
              try {
                await api.logoutEverywhere()
                const { clearAuth } = await import('../lib/auth')
                clearAuth()
                window.location.href = '/login'
              } catch { setLockMsg('Could not sign out everywhere — try again.') }
            }} className="text-sm px-4 py-2 rounded-xl border border-red-500/30 text-red-400/90 hover:bg-red-500/10">
              ⎋ Sign out on all devices
            </button>
            <p className="text-[11px] text-slate-600 mt-1.5">The real fix if a device is lost or a session may be compromised — it invalidates every existing sign-in instantly.</p>
          </div>
        </div>
      </div>

      {/* Storage (v0.10.0) */}
      <div className="card-glass p-6 mt-6">
        <h2 className="font-display text-lg font-semibold text-slate-100 mb-1">Storage</h2>
        {store ? (
          <>
            <div className="flex items-end justify-between mb-1.5">
              <p className="text-sm text-slate-300">
                <span className="text-gold-300 font-bold">{fmtBytes(store.used_bytes)}</span> of {fmtBytes(store.quota_bytes)} used
                <span className="text-slate-500"> · {store.plan} plan{store.bonus_bytes > 0 ? ` + ${fmtBytes(store.bonus_bytes)} purchased` : ''}</span>
              </p>
              <p className="text-[11px] text-slate-500">📸 {store.counts.photo} · 🎞 {store.counts.video} · 📜 {store.counts.document}</p>
            </div>
            <div className="h-1.5 rounded-full bg-white/[0.07] overflow-hidden mb-4">
              <div className={`h-full ${store.used_bytes / store.quota_bytes > 0.95 ? 'bg-red-500/80' : store.used_bytes / store.quota_bytes > 0.8 ? 'bg-amber-500/70' : 'bg-gradient-to-r from-emerald-500/70 to-gold-500/70'}`}
                style={{ width: `${Math.min(100, (store.used_bytes / store.quota_bytes) * 100)}%` }} />
            </div>
            {!hidePurchases() && <>
            <p className="text-xs text-slate-500 mb-2">Add space with Sage credits (credit-pack discount codes apply automatically when you buy credits):</p>
            <div className="flex flex-wrap gap-2">
              {store.packs.map(pk => (
                <button key={pk.id} onClick={async () => {
                  setStoreMsg('')
                  try {
                    const r = await api.buyStorage(pk.id)
                    setStoreMsg(`✨ ${pk.label} added — new reserve ${r.balance} credits.`)
                    api.getStorage().then(setStore).catch(() => {})
                    loadCredits()
                  } catch (e) { setStoreMsg(e instanceof Error ? e.message.replace(/^API \d+: /, '').replace(/[{}"]|error:/g, '') : 'Purchase failed') }
                }} className="text-left px-3.5 py-2.5 rounded-xl border border-white/10 bg-white/[0.03] hover:border-gold-500/40 transition-colors">
                  <p className="text-sm font-semibold text-slate-100">{pk.label}</p>
                  <p className="text-[11px] text-gold-300">{pk.credits} credits</p>
                </button>
              ))}
            </div>
            {storeMsg && <p className="text-xs text-gold-300 mt-3">{storeMsg}</p>}
            </>}
          </>
        ) : <p className="text-xs text-slate-500">Measuring your shelf of memories…</p>}
      </div>

      {/* Privacy & Data (GDPR self-service) */}
      <div className="card-glass p-6 mt-6">
        <h2 className="font-display text-lg font-semibold text-slate-100 mb-1">Privacy &amp; Data</h2>
        <p className="text-xs text-slate-500 mb-4">Your data is yours. Export it or erase it — no tickets, no waiting. <a href="/privacy" className="text-gold-400 underline">Privacy policy</a></p>
        <div className="flex gap-3 flex-wrap">
          <button onClick={() => api.downloadPrivacyExport().catch(() => setEraseMsg('Could not prepare the export — try again.'))} className="btn-ghost !py-2 text-sm">⬇ Download my data (JSON)</button>
          <button onClick={() => setShowErase(true)} className="text-sm px-4 py-2 rounded-xl border border-red-500/30 text-red-400/90 hover:bg-red-500/10">
            Erase my account…
          </button>
        </div>
      </div>

      {showErase && (
        <div className="fixed inset-0 z-50 bg-void-950/85 backdrop-blur-sm flex items-center justify-center px-4" onClick={() => setShowErase(false)}>
          <div className="card-glass !bg-void-900/95 p-6 w-full max-w-md agent-enter" onClick={e => e.stopPropagation()}>
            <h3 className="font-display text-lg font-bold text-red-300 mb-2">Erase everything, forever</h3>
            <p className="text-xs text-slate-400 mb-4">
              Destroys your account, every tree you own, all media (including encrypted vault items), chats, and memberships.
              Security logs are pseudonymized. This cannot be undone. Type <b className="text-slate-200">{user.email}</b> to confirm.
            </p>
            <input value={eraseConfirm} onChange={e => setEraseConfirm(e.target.value)} placeholder={user.email}
              className="input-dark mb-4" />
            {eraseMsg && <p className="text-xs text-red-300 mb-3">{eraseMsg}</p>}
            <div className="flex gap-3">
              <button
                disabled={eraseConfirm.trim().toLowerCase() !== user.email.toLowerCase() || erasing}
                onClick={async () => {
                  setErasing(true)
                  try {
                    await api.privacyErase(eraseConfirm.trim())
                    const { clearAuth } = await import('../lib/auth')
                    clearAuth()
                    window.location.href = '/'
                  } catch (e) {
                    setEraseMsg(e instanceof Error ? e.message.replace(/^API \d+: /, '').replace(/[{}"]|error:/g, '') : 'Erasure failed')
                    setErasing(false)
                  }
                }}
                className="flex-1 px-4 py-2.5 rounded-xl bg-red-500/20 border border-red-500/40 text-red-200 font-semibold disabled:opacity-40">
                {erasing ? 'Erasing…' : 'Erase my account'}
              </button>
              <button onClick={() => setShowErase(false)} className="btn-ghost">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
